import React, { useState, useEffect, useRef } from 'react';
import { Input, Space, Tag } from '@arco-design/web-react';
import { getUserSatisfactionApi } from '../../Api/api.js';

const colorMap = {
  green: { 
    tagBg: '#f6ffed', 
    tagColor: '#52c41a', 
    titleColor: '#52c41a',
    borderColor: '#b7eb8f'
  },
  red: { 
    tagBg: '#fff2f0', 
    tagColor: '#ff4d4f', 
    titleColor: '#ff4d4f',
    borderColor: '#ffccc7'
  },
  orange: { 
    tagBg: '#fff7e6', 
    tagColor: '#fa8c16', 
    titleColor: '#fa8c16',
    borderColor: '#ffd591'
  }
};

function TagGroup({ title, tags, color = 'green', style }) {
  const { tagBg, tagColor, titleColor, borderColor } = colorMap[color] || colorMap.green;
  
  return (
    <div style={{ 
      display: 'flex', 
      alignItems: 'flex-start', 
      marginTop:0 ,
      marginBottom: 3,
      padding: '0px 0',
      ...style 
    }}>
      <div style={{
        fontWeight: 350,
        color: titleColor,
        minWidth: 70,
        textAlign: 'left',
        fontSize: 14,
        lineHeight: '18px',
        paddingTop: 0
      }}>
        {title}
      </div>
      <div style={{ flex: 1 }}>
        <Space wrap size={8}>
          {tags.map((tag, index) => (
            <Tag 
              key={index} 
              color={color} 
              style={{ 
                background: tagBg, 
                color: tagColor,
                border: `1px solid ${borderColor}`,
                borderRadius: '6px',
                padding: '4px 12px',
                fontSize: '13px',
                fontWeight: 300,
                lineHeight: '18px',
                transition: 'all 0.2s ease',
                cursor: 'default',
                boxShadow: '0 1px 2px rgba(0,0,0,0.05)'
              }}
            >
              {tag}
            </Tag>
          ))}
        </Space>
      </div>
    </div>
  );
}

export default function InputWithTags({ 
  inputValue, 
  onInputChange, 
  placeholder = " ",
  inputStyle = {},
  satisfiedTags = [],
  unsatisfiedTags = [],
  questionTags = [],
  containerStyle = {},
  taskId = null, // 新增taskId参数
  containerWidth = 800 // 新增containerWidth参数，默认800px
}) {
  const [apiSatisfiedTags, setApiSatisfiedTags] = useState([]);
  const [apiUnsatisfiedTags, setApiUnsatisfiedTags] = useState([]);
  const [apiQuestionTags, setApiQuestionTags] = useState([]);
  const [loading, setLoading] = useState(false);
  const [rightSideHeight, setRightSideHeight] = useState(180);
  const rightSideRef = useRef(null);

  // 获取用户满意度数据
  useEffect(() => {
    if (taskId) {
      fetchUserSatisfaction(taskId);
    }
  }, [taskId]);

  // 监听右侧标签组高度变化
  useEffect(() => {
    if (!rightSideRef.current) return;

    const resizeObserver = new ResizeObserver((entries) => {
      for (let entry of entries) {
        const height = entry.contentRect.height;
        setRightSideHeight(Math.max(Math.min(height, 200), 50)); // 最小高度50px，最大高度250px
      }
    });

    resizeObserver.observe(rightSideRef.current);

    return () => {
      resizeObserver.disconnect();
    };
  }, [apiSatisfiedTags, apiUnsatisfiedTags, apiQuestionTags, loading]);

  const fetchUserSatisfaction = async (taskId) => {
    setLoading(true);
    try {
      const response = await getUserSatisfactionApi(taskId);
      if (response && response.status === 200 && response.data) {
        const data = response.data;
        
        // 解析满意点
        const satisfiedText = data['满意点'] || data['\u6ee1\u610f\u70b9'] || '';
        const satisfiedArray = satisfiedText ? satisfiedText.split(';').filter(item => item.trim()) : [];
        setApiSatisfiedTags(satisfiedArray);
        
        // 解析不满点
        const unsatisfiedText = data['不满点'] || data['\u4e0d\u6ee1\u70b9'] || '';
        const unsatisfiedArray = unsatisfiedText ? unsatisfiedText.split(';').filter(item => item.trim()) : [];
        setApiUnsatisfiedTags(unsatisfiedArray);
        
        // 解析疑问点
        const questionText = data['疑问点'] || data['\u7591\u95ee\u70b9'] || '';
        const questionArray = questionText ? questionText.split(';').filter(item => item.trim()) : [];
        setApiQuestionTags(questionArray);
      }
    } catch (error) {

    } finally {
      setLoading(false);
    }
  };

  // 合并API数据和传入的props数据
  const finalSatisfiedTags = [...satisfiedTags, ...apiSatisfiedTags];
  const finalUnsatisfiedTags = [...unsatisfiedTags, ...apiUnsatisfiedTags];
  const finalQuestionTags = [...questionTags, ...apiQuestionTags];

  // 移除了动态高度监听逻辑，因为左侧输入框使用固定高度
  return (
    <div style={{
      display: 'flex',
      justifyContent: 'flex-start',
      alignItems: 'flex-start',
      marginTop: 0,
      marginLeft: 10,
      ...containerStyle
    }}>
      {/* 左侧：输入框 */}
      <Input.TextArea
        placeholder={placeholder}
        style={{ 
          ...inputStyle,
          width: containerWidth || 800, 
          height: rightSideHeight,
          resize: 'none'
        }}
        value={inputValue}
        onChange={onInputChange}
      />
      
      {/* 右侧：标签组 */}
      <div ref={rightSideRef} style={{ 
        maxWidth: 790, 
        marginLeft: 60, 
        minHeight: 50,
        maxHeight: 200,
        overflowY: 'auto',
        paddingRight: 8
      }}>
        {loading && (
          <div style={{ textAlign: 'center', padding: '20px', color: '#999' }}>
            正在加载用户满意度数据...
          </div>
        )}
        {finalSatisfiedTags.length > 0 && (
          <TagGroup
            title="满意点"
            tags={finalSatisfiedTags}
            color="green"
          />
        )}
        {finalUnsatisfiedTags.length > 0 && (
          <TagGroup
            title="不满点"
            tags={finalUnsatisfiedTags}
            color="red"
          />
        )}
        {finalQuestionTags.length > 0 && (
          <TagGroup
            title="疑问点"
            tags={finalQuestionTags}
            color="orange"
            style={{ marginBottom: 0 }}
          />
        )}
      </div>
    </div>
  );
}